import { useState } from "react";
import { AlertTriangle, CheckCircle2, Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { flagFoundationARTax } from "@/lib/api";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const SEV = { flag: "text-rose-800 bg-rose-50 border-rose-200", warn: "text-amber-900 bg-amber-50 border-amber-200", info: "text-slate-600 bg-slate-50 border-slate-200" };

// Read-only tax check for one AR queue row: invoice vs work order vs the
// live jurisdiction lookup for the job site. "Flag to accounting" writes an
// AR note + audit row only — the invoice itself is never touched.
export function ARTaxAuditBadge({ invoiceId, audit, canWrite, onFlagged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState("");
  if (!audit) return null;
  const serious = audit.findings.filter((f) => f.severity !== "info");
  const ok = audit.status === "ok";

  const flag = async () => {
    setBusy(true);
    try {
      await flagFoundationARTax(invoiceId, comment);
      toast.success("Flagged to accounting — AR note added, invoice unchanged");
      setComment("");
      onFlagged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not flag to accounting");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-1.5" data-testid={`ar-tax-audit-${invoiceId}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
              className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border ${ok ? "text-emerald-800 bg-emerald-50 border-emerald-200" : "text-amber-900 bg-amber-50 border-amber-300 font-semibold"}`}
              data-testid={`ar-tax-audit-toggle-${invoiceId}`}>
        {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
        {ok ? "Tax check OK" : `Tax check: ${serious.length} finding${serious.length === 1 ? "" : "s"} — confirm with accounting`}
        {audit.flagged && <span className="ml-1 text-[9px] uppercase tracking-wide text-rose-700">· flagged {String(audit.flagged.at).slice(0, 10)}</span>}
      </button>
      {open && (
        <div className="mt-1 rounded-md border border-slate-200 bg-white p-2 text-[11px] space-y-1.5" data-testid={`ar-tax-audit-detail-${invoiceId}`}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 text-slate-700">
            <div><div className="text-[10px] uppercase text-slate-400">Invoice</div>tax {money(audit.invoice.tax_amount)} @ {audit.invoice.tax_rate}% · material {money(audit.invoice.taxable_material)}</div>
            <div><div className="text-[10px] uppercase text-slate-400">Work order {audit.work_order.wo_number || ""}</div>rate {audit.work_order.tax_rate}% · tax {money(audit.work_order.tax_amount)}{audit.work_order.tax_rate_manual ? " · manual" : ""}{audit.work_order.tax_authority ? ` · ${audit.work_order.tax_authority}` : ""}</div>
            <div><div className="text-[10px] uppercase text-slate-400">Site → jurisdiction</div>{[audit.site.city, audit.site.state].filter(Boolean).join(", ") || "no city/state"}{audit.jurisdiction ? ` → ${audit.jurisdiction.total_rate}% (${audit.jurisdiction.tax_no || "—"}) → expected ${money(audit.jurisdiction.expected_tax)}` : " → lookup unavailable"}</div>
          </div>
          <ul className="space-y-0.5">
            {audit.findings.map((f) => (
              <li key={f.code} className={`rounded border px-1.5 py-0.5 ${SEV[f.severity]}`} data-testid={`ar-tax-finding-${invoiceId}-${f.code}`}>
                <span className="font-mono text-[9px] uppercase mr-1">{f.severity}</span>{f.message}
              </li>
            ))}
            {audit.findings.length === 0 && <li className="text-emerald-700">Invoice, work order and jurisdiction agree.</li>}
          </ul>
          {audit.flagged && <div className="text-[10px] text-rose-700" data-testid={`ar-tax-flagged-${invoiceId}`}>Flagged by {audit.flagged.by} on {String(audit.flagged.at).slice(0, 16).replace("T", " ")}</div>}
          {canWrite && !ok && (
            <div className="flex items-center gap-1.5 pt-1 border-t border-slate-100">
              <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional note for accounting"
                     className="flex-1 h-7 px-2 text-[11px] border border-slate-300 rounded" data-testid={`ar-tax-flag-comment-${invoiceId}`} />
              <button type="button" disabled={busy} onClick={flag}
                      className="h-7 px-2 text-[11px] rounded border border-rose-300 text-rose-800 bg-rose-50 hover:bg-rose-100 inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid={`ar-tax-flag-btn-${invoiceId}`}>
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Flag className="w-3 h-3" />} Flag to accounting
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
